'use client'

import { SM_BREAKPOINT } from '@/app/constants'
import eventBus from '@/app/eventBus'
import { PS_EVENTS } from '@/app/ps-events'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'use-intl'

interface HotelHeaderNavigationProps {
	activeTab?: string
	isSticky?: boolean
	isVisible?: boolean
	onTabChange?: (tab: string) => void
}

const STICKY_HEADER_HEIGHT = 80

const HotelHeaderNavigation: FC<HotelHeaderNavigationProps> = ({
	activeTab = 'hotel-page-prices-section',
	onTabChange,
	isSticky = false,
	isVisible = true,
}) => {
	const [selectedTab, setSelectedTab] = useState(activeTab)
	const [isScrolling, setIsScrolling] = useState(false)
	const [manuallySelected, setManuallySelected] = useState(false)
	const lastScrollTop = useRef(0)
	const [showPrices, setShowPrices] = useState(true)
	const navigationContainerRef = useRef<HTMLDivElement>(null)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const t = useTranslations('Keywords')

	useEffect(() => {
		const handleNoRates = () => setShowPrices(false)
		eventBus.subscribe(PS_EVENTS.NO_HOTEL_RATES, handleNoRates)
		return () => {
			eventBus.unsubscribe(PS_EVENTS.NO_HOTEL_RATES, handleNoRates)
		}
	}, [])

	const navigationTabs = [
		{ id: 'hotel-page-photos-section', label: 'photos' },
		{ id: 'hotel-page-prices-section', label: 'prices' },
		{ id: 'hotel-page-amenities-section', label: 'amenities' },
		{
			id: 'hotel-page-overview-section',
			label: 'overview',
		},
		{
			id: 'hotel-page-policies-section',
			label: 'policies',
		},
		{ id: 'hotel-page-location-section', label: 'location' },
	]

	const tabs = useMemo(
		() =>
			navigationTabs.filter(
				(tab) =>
					// Keep prices only if we have rates
					(tab.id !== 'hotel-page-prices-section' || showPrices)
			),
		[showPrices, isMobile]
	)

	const scrollTabIntoView = useCallback((tabId: string) => {
		if (!navigationContainerRef.current) return

		const container = navigationContainerRef.current
		const tabButton = container.querySelector(
			`[data-tab-id="${tabId}"]`
		) as HTMLButtonElement

		if (!tabButton) return

		const containerRect = container.getBoundingClientRect()
		const tabRect = tabButton.getBoundingClientRect()

		// Check if tab is already fully visible
		const isFullyVisible =
			tabRect.left >= containerRect.left && tabRect.right <= containerRect.right

		if (!isFullyVisible) {
			const tabOffsetLeft = tabButton.offsetLeft
			const tabWidth = tabButton.offsetWidth
			const containerWidth = container.offsetWidth

			const targetScrollLeft = tabOffsetLeft - containerWidth / 2 + tabWidth / 2

			container.scrollTo({
				left: Math.max(0, targetScrollLeft),
				behavior: 'smooth',
			})
		}
	}, [])

	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId)
		if (element) {
			setIsScrolling(true)
			const headerOffset = isSticky ? STICKY_HEADER_HEIGHT : 0
			const elementPosition = element.offsetTop - headerOffset

			window.scrollTo({
				top: elementPosition,
				behavior: 'smooth',
			})

			setTimeout(() => setIsScrolling(false), 1000)
		}
	}

	const handleTabClick = (tabId: string) => {
		setSelectedTab(tabId)
		setManuallySelected(true) // Mark as manually selected
		onTabChange?.(tabId)

		// Special case: scroll to top for photos tab
		if (tabId === 'hotel-page-photos-section') {
			setIsScrolling(true)
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
			// Reset scrolling flag after animation completes
			setTimeout(() => setIsScrolling(false), 1000)
		} else {
			scrollToSection(tabId)
		}
	}

	const detectActiveSection = useCallback(() => {
		if (isScrolling || manuallySelected) return

		const headerOffset = isSticky ? STICKY_HEADER_HEIGHT : 0
		const scrollPosition = window.scrollY + headerOffset + 100

		let activeSection = tabs[0].id

		const isNearBottom =
			window.innerHeight + window.scrollY >= document.body.offsetHeight - 100

		if (isNearBottom) {
			// If near bottom, select the last tab
			activeSection = tabs[tabs.length - 1].id
		} else {
			// Find the section that best matches current scroll position
			for (let i = tabs.length - 1; i >= 0; i--) {
				const tab = tabs[i]
				const element = document.getElementById(tab.id)
				if (element) {
					const elementTop = element.offsetTop

					// If we've scrolled past the top of this section, it's active
					if (scrollPosition >= elementTop) {
						activeSection = tab.id
						break
					}
				}
			}
		}

		if (activeSection !== selectedTab) {
			setSelectedTab(activeSection)
			onTabChange?.(activeSection)
			scrollTabIntoView(activeSection)
		}
	}, [
		isScrolling,
		manuallySelected,
		isSticky,
		selectedTab,
		tabs,
		onTabChange,
		scrollTabIntoView,
	])

	const detectManualScroll = useCallback(() => {
		const currentScrollTop = window.scrollY

		if (
			!isScrolling &&
			Math.abs(currentScrollTop - lastScrollTop.current) > 10
		) {
			// If user is scrolling, reset manual selection
			if (manuallySelected) {
				setManuallySelected(false)
			}
		}

		lastScrollTop.current = currentScrollTop
	}, [isScrolling, manuallySelected])

	useEffect(() => {
		if (!isVisible) return

		const handleScroll = () => {
			detectManualScroll()
			detectActiveSection()
		}

		let ticking = false
		const throttledHandleScroll = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					handleScroll()
					ticking = false
				})
				ticking = true
			}
		}

		window.addEventListener('scroll', throttledHandleScroll, { passive: true })

		detectActiveSection()

		return () => {
			window.removeEventListener('scroll', throttledHandleScroll)
		}
	}, [isVisible, detectActiveSection, detectManualScroll])

	return (
		<nav
			className={classNames('hotel-header-navigation', {
				'hotel-header-navigation--sticky': isSticky,
				'hotel-header-navigation--hidden': !isVisible,
			})}
		>
			<div
				className="hotel-header-navigation__container container"
				ref={navigationContainerRef}
			>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						data-tab-id={tab.id}
						className={`hotel-header-navigation__tab ${selectedTab === tab.id
							? 'hotel-header-navigation__tab--selected'
							: ''
							}`}
						onClick={() => handleTabClick(tab.id)}
						type="button"
					>
						<span className="hotel-header-navigation__tab-text">
							{t(tab.label)}
						</span>
					</button>
				))}
			</div>
		</nav>
	)
}

export default dynamic(() => Promise.resolve(HotelHeaderNavigation), { ssr: false })
